// frontend/src/features/livestock/AssignPhotoView.tsx
// One inbox photo to sort (/livestock/photos/:id, plan 4.4): pick the
// organism (a recent chip, or search) or make it a tank shot (the chip pinned
// first, or in the search: first for "tank", "fts", "full", "whole"; tank
// shots design 6.1), optionally change the date, crop or add a note, then
// Assign. The view moves on to the next photo at once and the request
// finishes in the background (useInboxSorting); Later moves on, Discard
// throws the photo away. What was sorted, with Undo, shows in the
// action bar. Its own route, so the back gesture returns to the grid; moving
// between photos replaces the history entry.
//
// On a phone the bar holds the search field; tapping it turns the bar into a
// layer over the visible area (search mode), so the keyboard never covers the
// field or the results. A phone held sideways has the computer's two columns,
// with a phone's control sizes and without focusing the search.

import {
  useCallback,
  useDeferredValue,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from 'react';
import {
  ActionIcon,
  Alert,
  Avatar,
  Box,
  Button,
  Center,
  Chip,
  Container,
  Group,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  UnstyledButton,
  VisuallyHidden,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useElementSize } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconChevronLeft,
  IconChevronRight,
  IconContainer,
  IconCrop,
  IconNote,
  IconPhotoOff,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { photoInboxApi } from '@/api/photoInbox';
import { EmptyState } from '@/components/common/EmptyState';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import { useTank } from '@/contexts/TankContext';
import { useIsLandscapePhone } from '@/hooks/useIsLandscapePhone';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLivestockList } from '@/hooks/useLivestock';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { photoInboxKeys } from '@/hooks/photoInboxKeys';
import {
  useInboxCount,
  useInboxItem,
  usePrefetchInboxItem,
  useRefetchOnImageError,
} from '@/hooks/usePhotoInbox';
import type { Livestock, LivestockType } from '@/types/livestock';
import type { PhotoInboxDetail, PhotoInboxPage } from '@/types/photoInbox';
import type { LargePhoto } from '@/types/tankPhotos';
import { AddLivestockModal } from './AddLivestockModal';
import { AssignBottomBar } from './AssignBottomBar';
import { BRAND_TEXT } from './brandText';
import { CheckDateBadge } from './CheckDateBadge';
import { LIVESTOCK_TYPE_META } from './constants';
import { cropOutput, type CropFractions } from './cropOutput';
import type { InboxLocationState } from './InboxPhotoGrid';
import { OrganismResults } from './OrganismResults';
import {
  livestockTypesIn,
  rankOrganisms,
  tankShotPlace,
  type TankShotPlace,
} from './organismSearch';
import { PhotoCropModal } from './PhotoCropModal';
import { isNotFound } from './photoInboxErrors';
import {
  formatPhotoDay,
  formatTakenAt,
  isGuessedDate,
  localDay,
  pickerValueToIso,
  toPickerValue,
} from './photoInboxFormat';
import { showInboxNotice } from './photoInboxNotices';
import { readRecents } from './photoInboxRecents';
import { useSortRecords } from './photoInboxSorting';
import { PhotoLightbox } from './PhotoLightbox';
import {
  PICKER_THUMB_SIZES,
  readPickerThumbSize,
  writePickerThumbSize,
  type PickerThumbSize,
} from './pickerThumbSize';
import { SortNotice } from './SortNotice';
import { useInboxSorting, useSendingStatus } from './useInboxSorting';
import classes from './AssignPhotoView.module.css';

const EARLIEST_DATE = new Date(1990, 0, 1);

/** What the photo is to go to: an organism, the tank (a tank shot), or nothing yet. */
export type AssignSelection = { kind: 'organism'; id: number } | { kind: 'tank' } | null;

const TANK: AssignSelection = { kind: 'tank' };

/** One entry of the list the search drives: the tank shot, or an organism. */
type PickOption = { kind: 'tank' } | { kind: 'organism'; organism: Livestock };

const TANK_OPTION: PickOption = { kind: 'tank' };

function selectionOf(option: PickOption): AssignSelection {
  return option.kind === 'tank' ? TANK : { kind: 'organism', id: option.organism.id };
}

function isSelected(option: PickOption, selection: AssignSelection): boolean {
  if (!selection) return false;
  if (option.kind === 'tank') return selection.kind === 'tank';
  return selection.kind === 'organism' && selection.id === option.organism.id;
}

/**
 * The date picker's modal on a phone: the presets above the calendar rather
 * than beside it (beside, they pushed the last weekdays out of a 360 px
 * screen), and its buttons at least 44 px.
 */
const PHONE_DATE_PICKER_STYLES = {
  datePickerRoot: { flexDirection: 'column' },
  presetsList: {
    flexDirection: 'row',
    borderInlineEnd: 0,
    borderBottom: '1px solid var(--mantine-color-gray-2)',
    paddingInlineEnd: 0,
    marginInlineEnd: 0,
    paddingBottom: '.5em',
    marginBottom: '.5em',
  },
  presetButton: { minHeight: 44 },
  calendarHeaderControl: { width: 44, height: 44 },
  calendarHeaderLevel: { height: 44 },
  submitButton: { width: 44, height: 44, minWidth: 44, minHeight: 44 },
} satisfies Record<string, CSSProperties>;

/** The largest box of this aspect that fits in width x height. */
function fit(aspect: number, width: number, height: number): { width: number; height: number } {
  return width / height > aspect
    ? { width: height * aspect, height }
    : { width, height: width / aspect };
}

/** Positions the image so the box shows only the crop (the image is the master's aspect). */
function cropStyle(crop: CropFractions | null): CSSProperties {
  if (!crop) return { width: '100%', height: '100%', left: 0, top: 0 };
  return {
    width: `${100 / crop.w}%`,
    height: `${100 / crop.h}%`,
    left: `${(-crop.x / crop.w) * 100}%`,
    top: `${(-crop.y / crop.h) * 100}%`,
  };
}

/** "Euphyllia ancora · LPS", under an organism's name. */
function detailsOf(livestock: Livestock): string {
  const parts = livestock.scientific_name
    ? [livestock.scientific_name, livestock.category]
    : [livestock.category, livestock.subcategory];
  return parts.filter(Boolean).join(' · ');
}

/**
 * On a computer, the chosen organism's current photo over the photo's corner,
 * to compare; for a tank shot, the tank icon, to confirm the choice.
 */
function SelectedInset({ livestock }: { livestock: Livestock | null }): JSX.Element {
  if (!livestock) {
    return (
      <Box component="figure" className={classes.inset} aria-label="Selected: Tank shot">
        <Avatar size={98} radius="sm" color="teal">
          <IconContainer size={24} />
        </Avatar>
        <Text size="xs" fw={500} lineClamp={2} lh={1.25} mt={2}>
          Tank shot
        </Text>
      </Box>
    );
  }
  return (
    <Box
      component="figure"
      className={classes.inset}
      aria-label={`Selected: ${livestock.common_name}`}
    >
      {livestock.thumbnail_url ? (
        <img src={livestock.thumbnail_url} alt="" className={classes.insetImage} />
      ) : (
        <Avatar size={98} radius="sm" color="teal">
          {livestock.common_name.charAt(0)}
        </Avatar>
      )}
      {/* Two lines, so variants ("Green Acro (purple tips)") stay told apart */}
      <Text size="xs" fw={500} lineClamp={2} lh={1.25} mt={2}>
        {livestock.common_name}
      </Text>
    </Box>
  );
}

interface PhotoFrameProps {
  photo: PhotoInboxDetail | undefined;
  crop: CropFractions | null;
  /** Shown over the photo on a computer only; a phone shows it in the bar. */
  selected: Livestock | null;
  /** The tank shot is picked (shown over the photo like an organism). */
  tankPicked: boolean;
  isMobile: boolean;
  /** The frame and its measured size. */
  frameRef: RefObject<HTMLDivElement>;
  width: number;
  height: number;
  onPrevious: () => void;
  onNext: () => void;
  onView: () => void;
  onImageError: () => void;
  onReload: () => void;
}

function PhotoFrame({
  photo,
  crop,
  selected,
  tankPicked,
  isMobile,
  frameRef,
  width,
  height,
  onPrevious,
  onNext,
  onView,
  onImageError,
  onReload,
}: PhotoFrameProps): JSX.Element {
  const sourceAspect = photo?.width && photo.height ? photo.width / photo.height : 4 / 3;
  const shownAspect = crop ? (crop.w / crop.h) * sourceAspect : sourceAspect;
  const box = width > 0 && height > 0 ? fit(shownAspect, width, height) : null;

  return (
    <Box
      ref={frameRef}
      className={classes.frame}
      data-mobile={isMobile || undefined}
      data-desktop={!isMobile || undefined}
    >
      {!photo ? (
        <Skeleton w="100%" h="100%" radius={0} mih={200} />
      ) : photo.full_url ? (
        <UnstyledButton
          className={classes.picture}
          style={box ? { width: box.width, height: box.height } : { width: '100%', height: '100%' }}
          aria-label="View photo full screen"
          onClick={onView}
        >
          <img
            src={photo.full_url}
            alt={`Photo taken ${formatTakenAt(photo.taken_at)}`}
            style={cropStyle(crop)}
            onError={onImageError}
          />
        </UnstyledButton>
      ) : (
        <Center mih={200}>
          <Stack align="center" gap="xs">
            <IconPhotoOff size={24} color="var(--mantine-color-gray-7)" />
            <Button variant="subtle" size="compact-sm" h={44} onClick={onReload}>
              Reload photo
            </Button>
          </Stack>
        </Center>
      )}
      {photo?.prev_id != null && (
        <ActionIcon
          className={`${classes.nav} ${classes.prev}`}
          variant="white"
          color="dark"
          radius="xl"
          size={44}
          aria-label="Previous photo"
          onClick={onPrevious}
        >
          <IconChevronLeft size={16} />
        </ActionIcon>
      )}
      {photo?.next_id != null && (
        <ActionIcon
          className={`${classes.nav} ${classes.next}`}
          variant="white"
          color="dark"
          radius="xl"
          size={44}
          aria-label="Next photo"
          onClick={onNext}
        >
          <IconChevronRight size={16} />
        </ActionIcon>
      )}
      {(selected || tankPicked) && !isMobile && (
        <SelectedInset livestock={tankPicked ? null : selected} />
      )}
    </Box>
  );
}

interface AssignPhotoScreenProps {
  tankId: string;
  tankName: string;
  id: number;
  /** Moved on to this photo from another one in the view (not opened from the grid). */
  advanced: boolean;
  /** Says which photo this is, in the view's live region. */
  onArrive: (text: string) => void;
  typeFilter: LivestockType | null;
  onTypeFilter: (type: LivestockType | null) => void;
  /** The list's picture size on a computer; a phone always has small rows. */
  thumbSize: PickerThumbSize;
  onThumbSize: (size: PickerThumbSize) => void;
}

/** "Photo 3 of 20, 3 Aug 2025": its place among the photos still to sort, when the grid's pages are cached. */
function arrivalText(
  photo: PhotoInboxDetail,
  pages: InfiniteData<PhotoInboxPage> | undefined,
  sending: ReadonlySet<number>,
  toSort: number | undefined
): string {
  const day = formatPhotoDay(photo.taken_at);
  const ids = (pages?.pages ?? [])
    .flatMap((page) => page.items.map((item) => item.id))
    .filter((photoId) => !sending.has(photoId));
  const index = ids.indexOf(photo.id);
  const total = toSort !== undefined ? Math.max(toSort, ids.length) : ids.length;
  return index >= 0 ? `Photo ${index + 1} of ${total}, ${day}` : `Photo from ${day}`;
}

function AssignPhotoScreen({
  tankId,
  tankName,
  id,
  advanced,
  onArrive,
  typeFilter,
  onTypeFilter,
  thumbSize,
  onThumbSize,
}: AssignPhotoScreenProps): JSX.Element {
  // Phone sizes and touch behaviour (no search focused for a keyboard to cover the photo)...
  const isMobile = useIsMobile();
  // ...and the phone's layout (bottom bar, search mode), unless the phone is held sideways.
  const landscapePhone = useIsLandscapePhone();
  const phoneLayout = isMobile && !landscapePhone;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as InboxLocationState | null;
  const fromInbox = locationState?.fromInbox === true;

  const item = useInboxItem(id);
  const photo = item.data;
  const { data: left } = useInboxCount();
  const livestockQuery = useLivestockList();
  const allOrganisms = livestockQuery.data?.livestock;
  usePrefetchInboxItem(photo?.next_id ?? null);
  const onImageError = useRefetchOnImageError(item.refetch);

  const [selection, setSelectionState] = useState<AssignSelection>(null);
  // Whether the selection is the best match the typing picked, rather than
  // one picked by click, tap or arrow key.
  const pickedByTyping = useRef(false);
  const setSelection = useCallback((next: AssignSelection): void => {
    pickedByTyping.current = false;
    setSelectionState(next);
  }, []);
  /** The best match for what is typed, picked for Enter (on a computer). */
  const followTyping = (top: PickOption | undefined): void => {
    pickedByTyping.current = true;
    setSelectionState(top ? selectionOf(top) : null);
  };
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [entryDate, setEntryDate] = useState<string | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [crop, setCrop] = useState<CropFractions | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [added, setAdded] = useState<Livestock | null>(null);
  const [recents] = useState(() => readRecents(tankId));
  const [barHeight, setBarHeight] = useState(0);

  const searchRef = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const { ref: frameRef, width: frameWidth, height: frameHeight } = useElementSize();
  const dateRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const optionId = useCallback(
    (organismId: number): string => `${listId}-option-${organismId}`,
    [listId]
  );
  const tankOptionId = `${listId}-option-tank`;
  const idOf = (option: PickOption): string =>
    option.kind === 'tank' ? tankOptionId : optionId(option.organism.id);

  // Each photo opens at the top: the grid may have been scrolled far down.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const organisms = useMemo(() => {
    const list = allOrganisms ?? [];
    return added && !list.some((organism) => organism.id === added.id) ? [...list, added] : list;
  }, [allOrganisms, added]);
  const byId = useMemo(
    () => new Map(organisms.map((organism) => [organism.id, organism])),
    [organisms]
  );
  // Typing stays quick with a long list: the list follows a moment later.
  const listQuery = useDeferredValue(query);
  const results = useMemo(
    () => rankOrganisms(organisms, listQuery, typeFilter),
    [organisms, listQuery, typeFilter]
  );
  const types = useMemo(() => livestockTypesIn(organisms), [organisms]);
  const selectedId = selection?.kind === 'organism' ? selection.id : null;
  const selected = selectedId !== null ? (byId.get(selectedId) ?? null) : null;
  const tankPicked = selection?.kind === 'tank';
  // Something to assign to (an organism picked may have gone from the list meanwhile).
  const picked = tankPicked || selected !== null;

  // Where Tank shot goes in the list (tankShotPlace): first for "tank",
  // "fts", "full", "whole", after the organisms for the start of one, and on a
  // computer also first for an empty search (a phone has it in the chips).
  const tankPlace = (text: string): TankShotPlace | null =>
    !phoneLayout && !text.trim() ? 'first' : tankShotPlace(text);
  /** The list for this search, best first. */
  const optionsFor = (text: string, type: LivestockType | null): PickOption[] => {
    const place = tankPlace(text);
    const found = rankOrganisms(organisms, text, type).map(
      (organism): PickOption => ({ kind: 'organism', organism })
    );
    if (place === 'first') return [TANK_OPTION, ...found];
    return place === 'after' ? [...found, TANK_OPTION] : found;
  };
  const recentOrganisms = recents
    .map((organismId) => byId.get(organismId))
    .filter((organism): organism is Livestock => organism !== undefined);

  const sorting = useInboxSorting(tankId, organisms);
  const sending = useSendingStatus(tankId);
  // This photo's assign or discard, sent from here before and still on its way.
  const records = useSortRecords(tankId);
  const inFlight = records.find((record) => record.photoId === id && record.status === 'pending');

  // -- navigation: photos replace each other in history, so back leaves for the grid
  const openPhoto = useCallback(
    (target: number) => {
      const state: InboxLocationState = { fromInbox };
      navigate(`/livestock/photos/${target}`, { replace: true, state });
    },
    [navigate, fromInbox]
  );
  const openInbox = useCallback(() => {
    if (fromInbox) navigate(-1);
    else navigate('/livestock/photos', { replace: true });
  }, [navigate, fromInbox]);
  const goTo = useCallback(
    (target: number | null) => (target === null ? openInbox() : openPhoto(target)),
    [openInbox, openPhoto]
  );
  const later = useCallback(() => {
    if (photo) goTo(photo.next_id);
  }, [goTo, photo]);
  const previous = useCallback(() => {
    if (photo?.prev_id != null) openPhoto(photo.prev_id);
  }, [openPhoto, photo]);

  // -- sorting: on to the next photo straight away, the request in the background
  /** Assigns to what is picked. */
  const runAssign = (): void => {
    if (!photo || inFlight || !picked) return;
    // Only a changed date: echoing the capture date (to the millisecond) would drop its source.
    const fields = {
      ...(entryDate && { entry_date: pickerValueToIso(entryDate) }),
      ...(note.trim() && { entry_text: note.trim() }),
      ...(crop && { crop }),
    };
    // A tank shot sends no livestock_id at all; an organism names its target too.
    const sent = selected
      ? sorting.assign({
          photo,
          organism: selected,
          body: { target: 'organism', livestock_id: selected.id, ...fields },
        })
      : sorting.assign({ photo, body: { target: 'tank', ...fields } });
    if (sent) goTo(photo.next_id);
  };

  const runDiscard = (): void => {
    if (!photo || inFlight) return;
    if (sorting.discard(photo)) goTo(photo.next_id);
  };

  // -- picking
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;

  const exitSearch = useCallback((): void => {
    searchRef.current?.blur();
    setSearching(false);
    setAnnouncement('');
  }, []);

  // Stable, for the memoised result rows. The search is cleared, so the bar
  // and the list show the pick rather than what was typed.
  const pickSelection = useCallback(
    (next: AssignSelection): void => {
      setSelection(next);
      setQuery('');
      if (isMobileRef.current) exitSearch();
    },
    [exitSearch, setSelection]
  );
  const pickFromList = useCallback(
    (organism: Livestock): void => pickSelection({ kind: 'organism', id: organism.id }),
    [pickSelection]
  );
  const pickTank = useCallback((): void => pickSelection(TANK), [pickSelection]);
  const pickOption = (option: PickOption): void =>
    option.kind === 'tank' ? pickTank() : pickFromList(option.organism);

  // On a computer the best match follows the typing, so Enter assigns it.
  // Typing back to an empty search drops the match the typing picked, so a
  // habitual Enter there can't assign it (the tank shot tops that list).
  const changeQuery = (value: string): void => {
    setQuery(value);
    setAnnouncement('');
    if (phoneLayout) return;
    if (value.trim()) followTyping(optionsFor(value, typeFilter)[0]);
    else if (pickedByTyping.current) setSelection(null);
  };

  const changeType = (type: LivestockType | null): void => {
    onTypeFilter(type);
    if (!phoneLayout && query.trim()) followTyping(optionsFor(query, type)[0]);
  };

  const onSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
    // The list for what is typed now, not the deferred one drawn.
    const current = optionsFor(query, typeFilter);
    if (event.key === 'Escape' && searching) {
      event.preventDefault();
      exitSearch();
    } else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !phoneLayout) {
      event.preventDefault();
      if (current.length === 0) return;
      const index = current.findIndex((option) => isSelected(option, selection));
      const nextIndex =
        event.key === 'ArrowDown'
          ? Math.min(current.length - 1, index + 1)
          : Math.max(0, index < 0 ? 0 : index - 1);
      const option = current[nextIndex];
      if (!option) return;
      setSelection(selectionOf(option));
      document.getElementById(idOf(option))?.scrollIntoView?.({ block: 'nearest' });
    } else if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      if (phoneLayout) {
        // Go on the phone's keyboard: the top result.
        const top = current[0];
        if (top) pickOption(top);
        else if (query.trim()) setAnnouncement('No match');
        else exitSearch();
      } else if (
        picked &&
        (current.some((option) => isSelected(option, selection)) || !query.trim())
      ) {
        runAssign();
      } else if (query.trim()) {
        setAnnouncement('No match');
      }
    }
  };

  const onSearchFocus = (): void => {
    if (phoneLayout && !searching) setSearching(true);
  };

  // On a computer: search focused, Left/Right for previous/later (not while typing).
  useEffect(() => {
    if (!isMobile) searchRef.current?.focus();
  }, [isMobile]);

  const keys = useRef({ later, previous, blocked: false });
  keys.current = { later, previous, blocked: cropOpen || addOpen || dateOpen || viewOpen };
  useEffect(() => {
    if (phoneLayout) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (keys.current.blocked) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      const typing =
        !!target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
      const inEmptySearch = target === searchRef.current && searchRef.current?.value === '';
      if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && (!typing || inEmptySearch)) {
        event.preventDefault();
        if (event.key === 'ArrowLeft') keys.current.previous();
        else keys.current.later();
      } else if (!typing && event.key.length === 1 && event.key !== ' ') {
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [phoneLayout]);

  // Moved on to this photo: say which it is, and on a phone (where the button
  // tapped has gone with the last photo) put the focus on the heading. On a
  // computer the focus stays in the search.
  const toSortNow = left !== undefined ? Math.max(0, left - sending.count) : undefined;
  const arrived = useRef(false);
  useEffect(() => {
    if (!advanced || !photo || arrived.current) return;
    arrived.current = true;
    const sendingIds = new Set(
      records.filter((record) => record.status === 'pending').map((record) => record.photoId)
    );
    const pages = queryClient.getQueryData<InfiniteData<PhotoInboxPage>>(
      photoInboxKeys.list(tankId)
    );
    onArrive(arrivalText(photo, pages, sendingIds, toSortNow));
    if (isMobile) titleRef.current?.focus({ preventScroll: true });
  }, [advanced, photo, records, queryClient, tankId, toSortNow, onArrive, isMobile]);

  // Change opens the picker straight away.
  useEffect(() => {
    if (dateOpen) dateRef.current?.click();
  }, [dateOpen]);

  const photoDate = photo ? toPickerValue(photo.taken_at) : null;
  const changeDate = (value: string | null): void => {
    if (!value || !photoDate) {
      setEntryDate(null);
      return;
    }
    const unchanged = dayjs(value).isSame(dayjs(photoDate), 'minute');
    setEntryDate(unchanged ? null : value);
  };

  // A link from outside the app to a photo this tank's inbox doesn't have (sorted, or
  // another tank's): the grid, with a word. Within the app: "no longer in the inbox".
  const deepLinkGone = item.isError && isNotFound(item.error) && locationState === null;
  useEffect(() => {
    if (!deepLinkGone) return;
    showInboxNotice({ message: `That photo isn't in ${tankName}'s inbox.`, color: 'gray' });
    navigate('/livestock/photos', { replace: true });
  }, [deepLinkGone, tankName, navigate]);

  if (item.isError && isNotFound(item.error)) {
    return deepLinkGone ? <></> : <PhotoGone onBack={openInbox} />;
  }

  const toSort = toSortNow;
  const header = (
    <Group justify="space-between" wrap="nowrap" gap="xs">
      <Group gap={4} wrap="nowrap">
        <ActionIcon
          variant="subtle"
          color="gray"
          size={44}
          aria-label="Back to inbox"
          onClick={openInbox}
        >
          <IconArrowLeft size={16} />
        </ActionIcon>
        <Title order={1} fz={24} lh={1.2} ref={titleRef} tabIndex={-1}>
          Sort photos
        </Title>
      </Group>
      {toSort !== undefined && (
        <Text size="sm" c="gray.7" className={classes.tabular}>
          {`${toSort} left`}
        </Text>
      )}
    </Group>
  );

  const guessed = photo ? isGuessedDate(photo.taken_at_source) : false;
  const output =
    crop && photo?.width && photo.height
      ? cropOutput(crop, { width: photo.width, height: photo.height })
      : null;
  const controlHeight = isMobile ? 44 : undefined;
  const takenText = photo
    ? formatTakenAt(entryDate ? pickerValueToIso(entryDate) : photo.taken_at)
    : '';

  const details = photo ? (
    <Stack gap={isMobile ? 0 : 6}>
      <Group gap={6} wrap="wrap" align="center" mih={controlHeight}>
        <Text size="sm" fw={500} className={classes.tabular}>
          {takenText}
        </Text>
        <Text size="sm" c="gray.7" aria-hidden>
          ·
        </Text>
        {entryDate ? (
          <Text size="sm" c="gray.7">
            changed
          </Text>
        ) : guessed ? (
          <CheckDateBadge />
        ) : (
          <Text size="sm" c="gray.7">
            from photo
          </Text>
        )}
        <Button
          variant="subtle"
          size="compact-sm"
          h={controlHeight}
          px={6}
          aria-label="Change date"
          onClick={() => setDateOpen(true)}
          style={BRAND_TEXT}
        >
          Change
        </Button>
      </Group>
      {dateOpen && photoDate && (
        <DateTimePicker
          ref={dateRef}
          aria-label="Photo date"
          size="sm"
          value={entryDate ?? photoDate}
          onChange={changeDate}
          onDropdownClose={() => setDateOpen(false)}
          valueFormat="D MMM YYYY, HH:mm"
          minDate={EARLIEST_DATE}
          maxDate={new Date()}
          presets={[{ value: photoDate, label: 'Reset to photo date' }]}
          dropdownType={isMobile ? 'modal' : 'popover'}
          styles={isMobile ? PHONE_DATE_PICKER_STYLES : undefined}
        />
      )}
      <Group gap={4} wrap="wrap" align="center">
        <Button
          variant="subtle"
          size="compact-sm"
          h={controlHeight}
          leftSection={<IconCrop size={16} />}
          onClick={() => setCropOpen(true)}
          disabled={!photo.full_url}
          style={BRAND_TEXT}
        >
          {crop ? 'Edit crop' : 'Crop'}
        </Button>
        {crop && (
          <>
            {output && (
              <Text size="xs" c={output.low ? 'coral.6' : 'gray.7'} className={classes.tabular}>
                {output.label}
              </Text>
            )}
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              h={controlHeight}
              onClick={() => setCrop(null)}
            >
              Remove crop
            </Button>
          </>
        )}
        {!noteOpen && (
          <Button
            variant="subtle"
            size="compact-sm"
            h={controlHeight}
            leftSection={<IconNote size={16} />}
            onClick={() => setNoteOpen(true)}
            style={BRAND_TEXT}
          >
            Add note
          </Button>
        )}
      </Group>
      {noteOpen && (
        <Textarea
          aria-label="Note"
          placeholder="Note (optional)"
          autosize
          minRows={2}
          maxRows={5}
          maxLength={5000}
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
          // Revealed by the user's tap on Add note
          autoFocus
        />
      )}
    </Stack>
  ) : (
    <Skeleton h={20} w="60%" />
  );

  // Always shown, the tank pinned first (it is never one of the recents), so
  // "same as last" is the second chip.
  const recentChips = (
    <Box
      role="group"
      aria-label="Tank shot and recent organisms"
      className={phoneLayout ? classes.recentRow : classes.recentWrap}
    >
      <Button
        size={isMobile ? 'sm' : 'compact-sm'}
        h={controlHeight}
        radius="xl"
        variant={tankPicked ? 'filled' : 'light'}
        aria-pressed={tankPicked}
        className={classes.chip}
        style={tankPicked ? undefined : BRAND_TEXT}
        leftSection={<IconContainer size={16} />}
        onClick={() => setSelection(TANK)}
      >
        Tank shot
      </Button>
      {recentOrganisms.map((organism) => (
        <Button
          key={organism.id}
          size={isMobile ? 'sm' : 'compact-sm'}
          h={controlHeight}
          radius="xl"
          variant={organism.id === selectedId ? 'filled' : 'light'}
          aria-pressed={organism.id === selectedId}
          maw={240}
          className={classes.chip}
          style={organism.id === selectedId ? undefined : BRAND_TEXT}
          leftSection={
            organism.thumbnail_url ? (
              <Avatar src={organism.thumbnail_url} size={20} radius="xl" alt="" />
            ) : undefined
          }
          onClick={() => setSelection({ kind: 'organism', id: organism.id })}
        >
          {organism.common_name}
        </Button>
      ))}
    </Box>
  );

  const trimmedQuery = query.trim();
  const listShown = !phoneLayout || searching;
  // Tank shot in the list drawn (the deferred search), and the result Go picks on a phone.
  const listTankPlace = tankPlace(listQuery);
  const listTank = listTankPlace !== null;
  const tankOnTop = listTankPlace === 'first' || (listTankPlace === 'after' && !results.length);
  const goPicks = phoneLayout && !!trimmedQuery;
  const searchInput = (
    <TextInput
      ref={searchRef}
      className={classes.search}
      type="search"
      size={isMobile ? 'md' : 'sm'}
      role="combobox"
      aria-label="Search organisms"
      aria-controls={listShown ? listId : undefined}
      aria-expanded={listShown}
      aria-autocomplete="list"
      aria-activedescendant={
        !listShown
          ? undefined
          : tankPicked
            ? listTank
              ? tankOptionId
              : undefined
            : selectedId !== null && results.some((organism) => organism.id === selectedId)
              ? optionId(selectedId)
              : undefined
      }
      placeholder="Search organisms or tank shot"
      leftSection={<IconSearch size={16} />}
      value={query}
      onChange={(event) => changeQuery(event.currentTarget.value)}
      onKeyDown={onSearchKeyDown}
      onFocus={onSearchFocus}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="none"
      spellCheck={false}
      enterKeyHint={phoneLayout ? 'go' : 'done'}
      styles={isMobile ? { input: { height: 44, minHeight: 44 } } : undefined}
    />
  );

  // 36 px on a phone: easy to hit, still one compact row.
  const chipStyles = isMobile ? { label: { height: 36 } } : undefined;
  // One radio group: a single Tab stop, arrows move between the types.
  const chipName = `${listId}-type`;
  const typeChips =
    types.length > 1 ? (
      <Chip.Group
        multiple={false}
        value={typeFilter ?? 'all'}
        onChange={(value) => changeType(value === 'all' ? null : (value as LivestockType))}
      >
        <Group gap={6} wrap={phoneLayout ? 'nowrap' : 'wrap'} className={classes.typeChips}>
          <Chip
            value="all"
            name={chipName}
            size={isMobile ? 'md' : 'xs'}
            variant="light"
            styles={chipStyles}
          >
            All
          </Chip>
          {types.map((type) => (
            <Chip
              key={type}
              value={type}
              name={chipName}
              size={isMobile ? 'md' : 'xs'}
              variant="light"
              styles={chipStyles}
            >
              {LIVESTOCK_TYPE_META[type].label}
            </Chip>
          ))}
        </Group>
      </Chip.Group>
    ) : null;

  // On a computer: bigger pictures, to pick out a coral whose name doesn't come to mind.
  const sizeControl = isMobile ? null : (
    <SegmentedControl
      size="xs"
      ml="auto"
      value={thumbSize}
      onChange={(value) => onThumbSize(value as PickerThumbSize)}
      data={PICKER_THUMB_SIZES}
      aria-label="Picture size"
    />
  );

  const resultList = (
    <OrganismResults
      listId={listId}
      results={results}
      selectedId={selectedId}
      activeId={goPicks && !tankOnTop ? (results[0]?.id ?? null) : null}
      tankOption={
        listTank
          ? {
              id: tankOptionId,
              place: listTankPlace,
              selected: tankPicked,
              // On a phone, the result Go picks.
              active: goPicks && tankOnTop,
              onSelect: pickTank,
            }
          : null
      }
      optionId={optionId}
      onSelect={pickFromList}
      query={trimmedQuery}
      filterKey={typeFilter ?? 'all'}
      loading={livestockQuery.isLoading}
      loadFailed={livestockQuery.isError}
      onRetryLoad={() => void livestockQuery.refetch()}
      onAdd={() => setAddOpen(true)}
      scrolls
      thumbSize={isMobile ? 'small' : thumbSize}
    />
  );

  const liveRegion = (
    <VisuallyHidden aria-live="polite" role="status">
      {announcement}
    </VisuallyHidden>
  );

  const pendingHere = inFlight ? (
    <Text size="sm" c="gray.7" mih={controlHeight} className={classes.pendingHere}>
      {inFlight.kind === 'discard'
        ? 'Discarding…'
        : inFlight.target === 'tank'
          ? 'Adding as tank shot…'
          : `Assigning to ${inFlight.livestockName ?? 'an organism'}…`}
    </Text>
  ) : null;

  const primaryLabel = tankPicked
    ? 'Add as tank shot'
    : selected
      ? `Assign to ${selected.common_name}`
      : 'Pick an organism';
  const actions = (
    <Group gap="xs" wrap="nowrap" data-actions className={classes.actions}>
      <ActionIcon
        variant="default"
        size={isMobile ? 44 : 36}
        aria-label="Discard photo"
        onClick={runDiscard}
        disabled={!photo || !!inFlight}
        className={classes.discard}
      >
        <IconTrash size={16} color="var(--rdh-accent)" />
      </ActionIcon>
      <Button variant="default" h={isMobile ? 44 : 36} onClick={later} disabled={!photo}>
        Later
      </Button>
      <Button
        h={isMobile ? 44 : 36}
        className={classes.primary}
        // Two lines rather than "Assign to Green Acro (…": near-identical names differ at the end.
        styles={{ label: { whiteSpace: 'normal' } }}
        onClick={() => runAssign()}
        disabled={!photo || !picked || !!inFlight}
      >
        <span className={classes.primaryLabel}>{primaryLabel}</span>
      </Button>
    </Group>
  );

  const modals = (
    <>
      {photo?.full_url && (
        <PhotoCropModal
          mode="fractions"
          opened={cropOpen}
          imageUrl={photo.full_url}
          sourceSize={
            photo.width && photo.height ? { width: photo.width, height: photo.height } : null
          }
          initialCrop={crop}
          fullScreen={isMobile}
          onConfirm={(chosen) => {
            setCrop(chosen);
            setCropOpen(false);
          }}
          onCancel={() => setCropOpen(false)}
        />
      )}
      <PhotoLightbox
        photoUrl={photo?.full_url ?? null}
        photoKey={photo?.id}
        loadLarge={
          photo
            ? (): Promise<LargePhoto | null> => photoInboxApi.large(tankId, photo.id)
            : undefined
        }
        opened={viewOpen}
        onClose={() => setViewOpen(false)}
      />
      <AddLivestockModal
        opened={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={(createdOrganism) => {
          setAddOpen(false);
          if (createdOrganism) {
            setAdded(createdOrganism);
            setSelection({ kind: 'organism', id: createdOrganism.id });
            if (isMobile) exitSearch();
          }
        }}
        tankId={tankId}
        initialSearch={trimmedQuery}
        // Corals are what this is mostly for; a filter picked says otherwise.
        defaultType={typeFilter ?? 'coral'}
        // The selection shows the new organism; failures show clear of the action bar.
        notificationPosition="top-center"
        successNotice={false}
        fullScreen={isMobile}
        initialAcquisitionDate={
          photo ? localDay(entryDate ? pickerValueToIso(entryDate) : photo.taken_at) : null
        }
      />
    </>
  );

  const frame = (
    <PhotoFrame
      photo={photo}
      crop={crop}
      selected={selected}
      tankPicked={tankPicked}
      isMobile={phoneLayout}
      frameRef={frameRef}
      width={frameWidth}
      height={frameHeight}
      onPrevious={previous}
      onNext={later}
      onView={() => setViewOpen(true)}
      onImageError={onImageError}
      onReload={() => void item.refetch()}
    />
  );

  const loadError =
    item.isError && !isNotFound(item.error) ? (
      <Alert color="red" variant="light" p="xs" icon={<IconAlertCircle size={16} />}>
        <Text size="sm">{item.error.message}</Text>
        <Button
          mt={6}
          variant="filled"
          color="red"
          fullWidth={isMobile}
          h={controlHeight}
          size={isMobile ? 'sm' : 'compact-sm'}
          onClick={() => void item.refetch()}
        >
          Retry
        </Button>
      </Alert>
    ) : null;

  if (phoneLayout) {
    const selectedRow = tankPicked ? (
      <Group gap="sm" wrap="nowrap" className={classes.selectedRow}>
        <ThemeIcon variant="light" size={40} radius="sm" aria-hidden>
          <IconContainer size={20} />
        </ThemeIcon>
        <Text size="sm" fw={500} truncate lh={1.3} style={{ flex: 1, minWidth: 0 }}>
          Tank shot
        </Text>
        <Button
          variant="subtle"
          h={44}
          px="xs"
          aria-label="Change what to assign to"
          onClick={() => setSelection(null)}
          style={BRAND_TEXT}
        >
          Change
        </Button>
      </Group>
    ) : selected ? (
      <Group gap="sm" wrap="nowrap" className={classes.selectedRow}>
        <Avatar src={selected.thumbnail_url} size={40} radius="sm" alt="">
          {selected.common_name.charAt(0)}
        </Avatar>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={500} truncate lh={1.3}>
            {selected.common_name}
          </Text>
          {detailsOf(selected) && (
            <Text size="xs" c="gray.7" truncate lh={1.3}>
              {detailsOf(selected)}
            </Text>
          )}
        </Box>
        <Button
          variant="subtle"
          h={44}
          px="xs"
          aria-label="Change organism"
          onClick={() => setSelection(null)}
          style={BRAND_TEXT}
        >
          Change
        </Button>
      </Group>
    ) : (
      recentChips
    );

    const strip = (
      <Group gap="sm" wrap="nowrap" className={classes.strip}>
        {photo?.thumb_url ? (
          <img src={photo.thumb_url} alt="" className={classes.stripThumb} />
        ) : (
          <Box className={classes.stripThumb} />
        )}
        <Text
          size="sm"
          fw={500}
          className={classes.tabular}
          style={{ flex: 1, minWidth: 0 }}
          truncate
        >
          {takenText}
        </Text>
        <Button variant="subtle" h={44} px="sm" onClick={exitSearch} style={BRAND_TEXT}>
          Done
        </Button>
      </Group>
    );

    // One element, two modes: the slots keep the search field at the same place in
    // the tree, so it keeps its focus (and the keyboard) when search mode starts.
    return (
      <Box className={classes.phone}>
        <Box mb={4}>{header}</Box>
        {frame}
        <Stack gap={0} mt={4}>
          {loadError}
          {details}
        </Stack>
        <Box h={barHeight} aria-hidden />
        <AssignBottomBar searching={searching} onHeight={setBarHeight}>
          {searching ? null : <SortNotice tankId={tankId} sorting={sorting} />}
          {searching ? strip : (pendingHere ?? selectedRow)}
          {searchInput}
          {searching ? (
            <>
              {typeChips}
              {resultList}
            </>
          ) : (
            actions
          )}
          {liveRegion}
        </AssignBottomBar>
        {modals}
      </Box>
    );
  }

  return (
    <Box className={classes.desktop}>
      <Box className={classes.photoColumn}>{frame}</Box>
      <Box className={classes.panel}>
        {header}
        {loadError}
        {details}
        <Stack gap={4}>
          {recentOrganisms.length > 0 && (
            <Text size="xs" c="gray.7" fw={500}>
              Recent
            </Text>
          )}
          {recentChips}
        </Stack>
        {searchInput}
        {(typeChips || sizeControl) && (
          <Group gap="xs" align="flex-start" className={classes.listControls}>
            {typeChips}
            {sizeControl}
          </Group>
        )}
        {resultList}
        {liveRegion}
        <Stack gap="sm" className={classes.panelFoot}>
          <SortNotice tankId={tankId} sorting={sorting} compact />
          {pendingHere}
          {actions}
          {!isMobile && (
            <Text size="xs" c="gray.7">
              Type to search · Enter assigns · ← previous · → later
            </Text>
          )}
        </Stack>
      </Box>
      {modals}
    </Box>
  );
}

function PhotoGone({ onBack }: { onBack: () => void }): JSX.Element {
  return (
    <Container size="sm" py="xl">
      <EmptyState
        icon={IconPhotoOff}
        title="This photo is no longer in the inbox"
        description="It has been sorted or discarded already."
        primaryAction={{ label: 'Back to inbox', onClick: onBack }}
      />
    </Container>
  );
}

/** The route: one photo of the selected tank's inbox. */
export function AssignPhotoView(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const { selectedTank } = useTank();
  const navigate = useNavigate();
  // Kept from photo to photo: a run of corals stays filtered to corals.
  const [typeFilter, setTypeFilter] = useState<LivestockType | null>(null);
  // Kept from photo to photo, and from one visit to the next.
  const [thumbSize, setThumbSize] = useState<PickerThumbSize>(readPickerThumbSize);
  const changeThumbSize = useCallback((size: PickerThumbSize): void => {
    setThumbSize(size);
    writePickerThumbSize(size);
  }, []);
  const id = Number(idParam);
  // The photo first opened here; any other was moved on to, and is announced.
  const firstId = useRef(id);
  const [arrival, setArrival] = useState('');

  // Another tank selected while sorting (the header's tank menu): its grid.
  const openedIn = useRef<string | null>(null);
  if (openedIn.current === null && selectedTank) openedIn.current = selectedTank.id;
  const switched = !!selectedTank && selectedTank.id !== openedIn.current;
  useEffect(() => {
    if (!switched || !selectedTank) return;
    showInboxNotice({ message: `Showing ${selectedTank.name}'s photos to sort.`, color: 'gray' });
    navigate('/livestock/photos', { replace: true });
  }, [switched, selectedTank, navigate]);

  if (!selectedTank) {
    return (
      <Container size="lg" py="xl">
        <NoTankSelected purpose="to sort its photos" />
      </Container>
    );
  }
  if (switched) return <></>;
  if (!Number.isInteger(id) || id <= 0) {
    return <PhotoGone onBack={() => navigate('/livestock/photos', { replace: true })} />;
  }

  return (
    <>
      <AssignPhotoScreen
        key={id}
        tankId={selectedTank.id}
        tankName={selectedTank.name}
        id={id}
        advanced={id !== firstId.current}
        onArrive={setArrival}
        typeFilter={typeFilter}
        onTypeFilter={setTypeFilter}
        thumbSize={thumbSize}
        onThumbSize={changeThumbSize}
      />
      {/* Outside the screen, which is replaced for each photo: a live region must stay put. */}
      <VisuallyHidden aria-live="polite" role="status">
        {arrival}
      </VisuallyHidden>
    </>
  );
}
